import BigDecimal from 'js-big-decimal'

export type Decimal = BigDecimal | number | string

export const toBigDecimal = (num: Decimal | bigint) => {
  if (typeof num === 'bigint') {
    return new BigDecimal(num.toString())
  }
  return num instanceof BigDecimal ? num : new BigDecimal(num)
}

const periodParams = {
  ones: { significantDigits: 0, sign: '' },
  thousands: { significantDigits: 3, sign: 'K' },
  millions: { significantDigits: 6, sign: 'M' },
}
/**
 * @description Convert to number with length less than or equal 4
 * * `125.105    => 125.1`
 * * `8953.41    => 8953`
 * * `41942.12   => 41.9K`
 * * `271245.94  => 271.2K`
 * * `7892143.12 => 7.9M`
 */
export const toShortFormat = (number: number, period: keyof typeof periodParams) => {
  const { significantDigits, sign } = periodParams[period]
  const result = (number / 10 ** significantDigits).toFixed(2).slice(0, -1)

  const countNumbers = (result.match(/\d/g) || []).length
  if (countNumbers > 4) {
    const trimmedResult = Math.trunc(Number(result))
    return `${Number(trimmedResult)}${sign}`
  }
  return `${Number(result)}${sign}`
}

export const moreThan = (a: Decimal, b: Decimal) => {
  a = toBigDecimal(a)
  b = toBigDecimal(b)

  return a.compareTo(b) === 1
}

export const lessThan = (a: Decimal, b: Decimal) => {
  a = toBigDecimal(a)
  b = toBigDecimal(b)

  return a.compareTo(b) === -1
}

/**
 * @description a + b
 */
export const add = (a: Decimal, b: Decimal) => {
  a = toBigDecimal(a)
  b = toBigDecimal(b)

  return a.add(b)
}

/**
 * @description a / b
 */
export const divide = (a: Decimal, b: Decimal, precision?: number) => {
  a = toBigDecimal(a)
  b = toBigDecimal(b)

  return a.divide(b, precision)
}

/**
 * @description a * b
 */
export const multiply = (a: Decimal, b: Decimal) => {
  a = toBigDecimal(a)
  b = toBigDecimal(b)

  return a.multiply(b)
}

/**
 * @description a - b
 */
export const subtract = (a: Decimal, b: Decimal) => {
  a = toBigDecimal(a)
  b = toBigDecimal(b)

  return a.subtract(b)
}

export const isNumber = (str: string | number = ''): boolean => {
  if (str === '') {
    return true
  }
  return !Number.isNaN(str) && !Number.isNaN(parseFloat(str as string))
}

/**
 * @description Trim to `number` fractional significant digits
 * @param number Absolute value of the number must be less than 1
 * @example
 * 0.000457491932 => 0.000457, significantDigits = 3
 * -0.02601210511 => -0.026, significantDigits = 3
 * -24.1452578850 => -24.14525, significantDigits = 5
 */
export const trimToFractionalSignificantDigits = (number: string, significantDigits: number) => {
  const regex = new RegExp(`.*\\.0*[1-9]{1,${significantDigits}}`)
  return number.match(regex)
}

export const limitNumberWithinRange = (value: number, min: number, max: number) => {
  const MIN = min || 1
  const MAX = max || 20
  return Math.min(Math.max(value, MIN), MAX)
}
